/**
 * ============================================================================
 * ⭐⭐ WHAT A `dock.DriverCheckInEvent` SAYS TO A PERSON — ONE MAP, TWO READERS
 * ============================================================================
 *
 * ⛔⛔ EXTRACTED FROM `CheckInPanel.tsx` ON 2026-09-20, AND THE MOVE FOUND A
 * SILENT DOWNGRADE THAT THE MOVE ITSELF WOULD HAVE CAUSED.
 *
 * ⭐ Pete: *"The Arrival History ... doesn't need to be on the card anymore because
 * single clicking the driver will show the history in the history panel."* ✅ Right —
 * `HistoryPanel` does call `GET mgmt/checkins/{id}/history` for a driver focus.
 *
 * ⛔⛔ **BUT IT RENDERED `e.notes?.trim() || e.eventType` — THE RAW CODE.** The card
 * said *"Checked in at the gate"*; the rail says `CheckedIn`. Deleting the card
 * without moving this map would have quietly replaced sentences with identifiers,
 * and the only evidence would have been a screenshot nobody compared.
 *   ** MOVING A FEATURE TO A BETTER HOME IS ONLY AN IMPROVEMENT IF THE NEW HOME
 *      CARRIES EVERYTHING THE OLD ONE DID. CHECK WHAT THE OLD ONE DID. **
 *
 * ⚠️ AND `HistoryPanel`'s OWN COMMENT NAMED THIS FUNCTION while implementing a
 * weaker copy of it — *"the same rule `checkInEventLabel` follows on the card"*. A
 * comment citing the thing it is duplicating is the clearest possible sign that the
 * two should have been one.
 *   ** A COMMENT THAT CITES ANOTHER IMPLEMENTATION IS A MISSING IMPORT. **
 *
 * ⬜ NOT DONE HERE, AND DELIBERATELY: `AppointmentEventDto` has NO wording map at
 * all — `WorkerStarted`, `DocumentFiled`, `StatusChanged` print raw in the rail
 * today, which is what Pete's 2026-09-20 screenshot shows. Inventing that wording is
 * a design decision, not a refactor, so it is recorded rather than guessed.
 */
import type { CheckInEventDto } from '@/api/client';

/**
 * Turn a `dock.DriverCheckInEvent` row into something a scheduler reads.
 *
 * ⚠️ FALLS BACK TO THE RAW TYPE rather than hiding an event it does not recognise.
 * A log that silently drops rows it has no wording for is worse than one that prints
 * a code — that is exactly how the driver-text failures stayed invisible (#133).
 */
export function checkInEventLabel(e: CheckInEventDto): string {
    const f = e.fieldName ? ` ${e.fieldName}` : '';
    switch (e.eventType) {
        case 'CheckedIn':      return 'Checked in at the gate';
        case 'Matched':        return e.notes || 'Matched to an appointment';
        case 'Unmatched':      return 'Detached from its appointment';
        case 'Edited':         return `Edited${f}${e.oldValue || e.newValue ? `: ${e.oldValue ?? '—'} → ${e.newValue ?? '—'}` : ''}`;
        case 'Voided':         return `Voided${e.notes ? `: ${e.notes}` : ''}`;
        case 'PalletVariance': return `Pallet count differs: ${e.oldValue ?? '—'} → ${e.newValue ?? '—'}`;
        case 'LicenceScanned':
        case 'LicenseScanned': return 'License scanned';
        case 'LicenceReplaced':
        case 'LicenseReplaced': return e.notes || 'License replaced';
        /**
          * ⭐ THE SECOND VISIT (2026-08-27). Pete: *"We need him to enter his cell
          * number on the tablet, but not scan his license again — redundant."*
          *
          * ⛔ THE REUSE LINE IS NOT OPTIONAL. Without it the card shows a licence on
          * file and NO event that put it there, which reads as a system that cannot
          * account for its own evidence. ⚠️ Both spellings, like every other licence
          * event here — this codebase writes both and always has.
          */
        case 'LicenceReused':
        case 'LicenseReused':  return e.notes || 'License already on file — not rescanned';
        case 'LicenceRescanNeeded':
        case 'LicenseRescanNeeded': return e.notes || 'License on file could not be reused';
        case 'FoodSafetyAck':  return 'Food-safety notice acknowledged';
        // ⭐ TEXTS (#150). ⛔ Sent and Failed are separate types on purpose: the
        // renderer must not have to read a payload to know which happened, which
        // is exactly how a broken SMS path wore the name "Sent" for hours (#133).
        case 'DoorTextSent':      return e.notes || 'Door text sent';
        case 'DoorTextFailed':    return e.notes || 'Door text FAILED';
        case 'PaperworkTextSent': return e.notes || 'Paperwork text sent';
        case 'PaperworkTextFailed': return e.notes || 'Paperwork text FAILED';
        // ⭐ PAPERWORK (#145). Written on the arrival's log as well as the
        // appointment's, so this panel can answer "what has happened to this truck"
        // without the scheduler opening the appointment to find half the answer.
        case 'DocumentFiled':  return e.notes || `Paperwork filed${f}`;
        case 'DocumentSigned': return e.notes || 'Paperwork signed as received';
        case 'DocumentVoided': return e.notes || `Paperwork removed${f}`;
        default:               return e.notes || e.eventType;
    }
}
